import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import DownloadIcon from "@atlaskit/icon/core/download";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import ShowMoreVerticalIcon from "@atlaskit/icon/core/show-more-vertical";
import {token} from "@atlaskit/tokens";
import {useQueryClient} from "@tanstack/react-query";
import {createFileRoute, Navigate} from "@tanstack/react-router";
import React, {useCallback, useMemo, useState} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import {ExportOptionsDrawerContent} from "~/components/dataActions/ExportOptionsDrawerContent";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {OttopilotDataViewer} from "~/components/ottopilotDataViewer/ottopilotDataViewer";
import {useOttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotExportActions} from "~/components/ottopilotDataViewer/useOttopilotExportActions";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {TableOnlyPage} from "~/components/tableOnlyPage";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useOrganizationalUnitTemplates, useWorkspace} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {
    OrganizationalUnitStatus,
    type OrganizationalUnit,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {SharedCalendarViewConfig} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {
    EmotoradOus,
    EMOTORAD_DEALERS_NAV_VISIBLE_ROLE_TEMPLATE_IDS,
    EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PINCODE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__STATE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
    EMOTORAD_DEALERSHIP_TABLE_ADDITIONAL_COLUMN_IDS,
    isEmotoradWorkspace,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {EmotoradDealerUpdatePanel} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/emotorad/dealers/index/-emotorad-dealer-update-panel";
import {EmotoradDealerViewWithFooterComponent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/emotorad/dealers/index/-emotorad-dealer-view-panel";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    convertOttopilotViewerFiltersToPlutoComplexFilter,
    convertOttopilotViewerSortToPlutoOrderBy,
    getOrganizationalUnits,
    getOrganizationalUnitsCount,
} from "~/utilities/queries";
import {
    collectOrganisationalUnitIdsAccessibleToUser,
    filterConfigurationWithOrganisationalUnitAccessScope,
} from "~/utilities/accessControl/userOuAccess";
import {WORKSPACE__ORGANIZATION_UNITS__QUERY_KEY} from "~/utilities/queryKeys";
import {
    resolveCalendarEventTypeColor,
    resolveCalendarEventTypeLabel,
    resolveCalendarLegendItems,
} from "~/utilities/sharedCalendarConfig";
import {formatDateAndTime} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {uniqueUuid} from "@vesta/unique/unique";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/emotorad/dealers/",
)({
    loader: async () => {},
    component: EmotoradDealersRouteComponent,
});

function EmotoradDealersRouteComponent() {
    const {workspace} = useWorkspace();
    const {workspaceUser} = useWorkspaceUser();
    const canAccessEmotoradDealers =
        isEmotoradWorkspace(workspace) !== true ||
        EMOTORAD_DEALERS_NAV_VISIBLE_ROLE_TEMPLATE_IDS.has(workspaceUser.templateId);

    if (canAccessEmotoradDealers !== true) {
        return <Navigate to="/w/$workspaceId" params={{workspaceId: workspace.id}} />;
    }

    return <RouteComponent />;
}

const DEALERS_TABLE_VIEW_ID = uniqueUuid`47798d26-1f6a-47b8-bf96-5bb65f12a0e9`;
const DEALERS_KANBAN_VIEW_ID = uniqueUuid`emotorad-dealers-kanban-view-id`;
const DEALERS_CALENDAR_VIEW_ID = uniqueUuid`emotorad-dealers-calendar-view-id`;
const EMOTORAD_DEALERSHIP_TEMPLATE_ID = EmotoradOus.Dealership as Uuid;
const DEALERS_DATA_VIEWER_QUERY_KEY_PREFIX = [uniqueUuid`447defe0-8d36-4198-8624-81b2b2bbe222`, "emotorad-dealers"];

const EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG: SharedCalendarViewConfig = {
    eventTypeLabelMap: {
        [OrganizationalUnitStatus.active]: "Active",
        [OrganizationalUnitStatus.deleted]: "Suspended",
    },
    legendItems: [
        {type: OrganizationalUnitStatus.active, label: "Active", description: "Dealer is active"},
        {type: OrganizationalUnitStatus.deleted, label: "Suspended", description: "Dealer is suspended"},
    ],
    eventTypeColorMap: {
        [OrganizationalUnitStatus.active]: {
            backgroundColorToken: "color.background.accent.green.subtlest",
            borderColorToken: "color.border.accent.green",
            textColorToken: "color.text.accent.green",
        },
        [OrganizationalUnitStatus.deleted]: {
            backgroundColorToken: "color.background.accent.yellow.subtlest",
            borderColorToken: "color.border.accent.yellow",
            textColorToken: "color.text.accent.yellow",
        },
    },
};

function emotoradDealerStatusToDisplayName(status: OrganizationalUnitStatus): string {
    if (status === OrganizationalUnitStatus.active) {
        return "Active";
    }
    if (status === OrganizationalUnitStatus.deleted) {
        return "Suspended";
    }
    return "Unknown";
}

function getDealerDisplayName(
    organizationalUnit: OrganizationalUnit,
    organizationalUnitTemplates: ReadonlyArray<OrganizationalUnitTemplate>,
): string {
    const template = organizationalUnitTemplates.find((t) => t.id === organizationalUnit.templateId);
    if (template === undefined) {
        return "?";
    }
    return processOttopilotStringTemplate(template.instanceDisplayNameTemplate, organizationalUnit.additionalColumnValues);
}

const EMOTORAD_DEALER_SEARCH_COLUMNS: Array<string> = [
    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__STATE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__PINCODE__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__OWNER_EMAIL__COLUMN_ID,
];

const EMOTORAD_DEALER_DEFAULT_VISIBLE_FILTER_COLUMN_IDS: Array<string> = [
    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__STATE__COLUMN_ID,
    EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID,
    EMOTORAD__OU_DEALERSHIP_FRANCHISE__COLUMN_ID,
];

function unknownToFilterableString(raw: unknown): string {
    if (raw === null || raw === undefined) {
        return "";
    }
    if (typeof raw === "string") {
        return raw.trim();
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    if (typeof raw === "object") {
        const record = raw as Record<string, unknown>;
        if (typeof record.value === "string" && record.value.trim() !== "") {
            return record.value.trim();
        }
        if (typeof record.displayName === "string" && record.displayName.trim() !== "") {
            return record.displayName.trim();
        }
        if (typeof record.label === "string" && record.label.trim() !== "") {
            return record.label.trim();
        }
    }
    return "";
}

function getEmotoradDealershipOrganizationalUnits(
    organizationalUnits: Array<OrganizationalUnit>,
): Array<OrganizationalUnit> {
    return organizationalUnits.filter((organizationalUnit) => organizationalUnit.templateId === EMOTORAD_DEALERSHIP_TEMPLATE_ID);
}

function getDealershipOrganizationalUnitDisplayName(
    organizationalUnit: OrganizationalUnit,
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
): string {
    const nameFromColumn = unknownToFilterableString(
        organizationalUnit.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID],
    );
    if (nameFromColumn !== "") {
        return nameFromColumn;
    }

    const organizationalUnitTemplate = organizationalUnitTemplates.find(
        (template) => template.id === organizationalUnit.templateId,
    );
    if (organizationalUnitTemplate === undefined) {
        return "?";
    }

    return processOttopilotStringTemplate(
        organizationalUnitTemplate.instanceDisplayNameTemplate,
        organizationalUnit.additionalColumnValues,
    );
}

function collectDealershipColumnValues(
    dealerships: Array<OrganizationalUnit>,
    columnId: string,
): Array<string> {
    const values = new Set<string>();
    for (const dealership of dealerships) {
        const raw =
            columnId === EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID
                ? dealership.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID]
                : dealership.additionalColumnValues[columnId as Uuid];
        const value = unknownToFilterableString(raw);
        if (value !== "") {
            values.add(value);
        }
    }
    return [...values].sort((left, right) => left.localeCompare(right));
}

function filterDealershipShowroomNameColumnValues(params: {
    fetchedValues: Array<string>;
    dealerships: Array<OrganizationalUnit>;
    accessibleOrganisationalUnitIds: null | Array<Uuid>;
}): Array<string> {
    const {fetchedValues, dealerships, accessibleOrganisationalUnitIds} = params;
    if (accessibleOrganisationalUnitIds === null) {
        return fetchedValues;
    }

    const accessibleOuIds = new Set(accessibleOrganisationalUnitIds);
    const allowedNames = new Set<string>();
    for (const dealership of dealerships) {
        if (accessibleOuIds.has(dealership.id) !== true) {
            continue;
        }
        const name = unknownToFilterableString(
            dealership.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID],
        );
        if (name !== "") {
            allowedNames.add(name);
        }
    }

    return fetchedValues.filter((value) => allowedNames.has(value.trim()));
}

function collectDealershipParentIds(dealerships: Array<OrganizationalUnit>): Array<string> {
    const parentIds = new Set<string>();
    for (const dealership of dealerships) {
        if (dealership.parentId !== null) {
            parentIds.add(dealership.parentId);
        }
    }
    return [...parentIds].sort((left, right) => left.localeCompare(right));
}

function filterConfigurationWithLockedDealershipTemplate(
    filterConfiguration: FilterConfiguration,
    dealershipTemplateId: Uuid,
): FilterConfiguration {
    const lineItemsWithoutTemplate = filterConfiguration.lineItems.filter((item) => item.columnId !== "templateId");
    return {
        revisionId: filterConfiguration.revisionId,
        lineItems: [
            {
                columnId: "templateId",
                operation: FilterOperation.equals,
                value: dealershipTemplateId,
            },
            ...lineItemsWithoutTemplate,
        ],
    };
}

function RouteComponent() {
    const queryClient = useQueryClient();
    const isMobile = useCheckMobile();
    const {showFlag} = useAnankeFlags();
    const [exportDrawerOpen, setExportDrawerOpen] = useState(false);
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {workspace} = useWorkspace();

    const invalidateDealersQuery = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({queryKey: ["ottopilotDataViewer", ...DEALERS_DATA_VIEWER_QUERY_KEY_PREFIX]}),
            queryClient.invalidateQueries({queryKey: ["ottopilotDataViewer"]}),
            queryClient.invalidateQueries({queryKey: [WORKSPACE__ORGANIZATION_UNITS__QUERY_KEY, workspace.id]}),
        ]);
    }, [queryClient, workspace.id]);

    const dealerships = useMemo(
        () => getEmotoradDealershipOrganizationalUnits(organizationalUnits),
        [organizationalUnits],
    );

    const accessibleDealershipOrganisationalUnitIds = useMemo(() => {
        return collectOrganisationalUnitIdsAccessibleToUser({
            targetUser: workspaceUser,
            users: users,
            userTemplates: userTemplates,
            organizationalUnits: organizationalUnits,
            organizationalUnitTemplates: organizationalUnitTemplates,
            candidateOrganisationalUnits: dealerships,
        });
    }, [
        workspaceUser,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        dealerships,
    ]);

    const baseDealerColumns = useMemo(
        (): Array<OttopilotColumn<OrganizationalUnit>> => [
            {
                id: EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
                type: OttopilotColumnType.string,
                displayName: "Dealer name",
                valueGetter: (organizationalUnit) =>
                    getDealershipOrganizationalUnitDisplayName(
                        organizationalUnit,
                        organizationalUnitTemplates,
                    ),
                sortingCapability: false,
                filteringCapability_contains: true,
            },
            {
                id: "parentId",
                type: OttopilotColumnType.uuidNullable,
                displayName: "Parent",
                valueGetter: (organizationalUnit) => organizationalUnit.parentId,
                valueFormatter: (value) => {
                    if (value === null) {
                        return "-";
                    }

                    const parentOrganizationalUnit = organizationalUnits.find(
                        (organizationalUnit_) => organizationalUnit_.id === value,
                    );
                    if (parentOrganizationalUnit === undefined) {
                        return "?";
                    }

                    return getDealershipOrganizationalUnitDisplayName(
                        parentOrganizationalUnit,
                        organizationalUnitTemplates,
                    );
                },
                sortingCapability: false,
                filteringCapability_in: {
                    valuesQueryFn: async () => collectDealershipParentIds(dealerships),
                },
            },
        ],
        [dealerships, organizationalUnits, organizationalUnitTemplates],
    );

    const emotoradDealerAdditionalColumns = useMemo((): Array<OttopilotColumn<OrganizationalUnit>> => {
        const dealershipTemplate = organizationalUnitTemplates.find(
            (template) => template.id === EMOTORAD_DEALERSHIP_TEMPLATE_ID,
        );
        if (dealershipTemplate === undefined) {
            return [];
        }

        return EMOTORAD_DEALERSHIP_TABLE_ADDITIONAL_COLUMN_IDS
            .map((columnId) => dealershipTemplate.additionalColumns.find((column) => column.id === columnId))
            .filter((column): column is NonNullable<typeof column> => column !== undefined)
            .map((column) => ({
                id: column.id,
                type: OttopilotColumnType.string,
                displayName: column.displayName,
                valueGetter: (organizationalUnit: OrganizationalUnit) => {
                    const display = unknownToFilterableString(organizationalUnit.additionalColumnValues[column.id]);
                    return display === "" ? "-" : display;
                },
                sortingCapability: false,
                filteringCapability_contains: true,
            }));
    }, [organizationalUnitTemplates]);

    const dealerStatusColumn = useMemo(
        (): OttopilotColumn<OrganizationalUnit> => ({
            id: "status",
            type: OttopilotColumnType.string,
            displayName: "Status",
            valueGetter: (organizationalUnit) => organizationalUnit.status,
            valueFormatter: (value) => emotoradDealerStatusToDisplayName(value as OrganizationalUnitStatus),
            sortingCapability: false,
            filteringCapability_in: {
                valuesQueryFn: async () => [OrganizationalUnitStatus.active, OrganizationalUnitStatus.deleted],
            },
        }),
        [],
    );

    const dealerTableColumns = useMemo(
        () => [...baseDealerColumns, dealerStatusColumn, ...emotoradDealerAdditionalColumns],
        [baseDealerColumns, dealerStatusColumn, emotoradDealerAdditionalColumns],
    );

    const dealersCalendarLegendItems = useMemo(
        () =>
            resolveCalendarLegendItems(
                EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG,
                EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG,
            ),
        [],
    );
    const getDealersCalendarTypeLabel = useCallback((type: string): string => {
        return resolveCalendarEventTypeLabel(
            type,
            EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG,
            EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG,
            "Unknown",
        );
    }, []);
    const getDealersCalendarTypeColor = useCallback((type: string | undefined) => {
        return resolveCalendarEventTypeColor(
            type,
            EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG,
            EMOTORAD_DEALERS_STATUS_CALENDAR_CONFIG,
        );
    }, []);

    const dealersDataViewerController = useOttopilotDataViewerController<OrganizationalUnit>({
        rowDisplayNameVariants: {
            singularSentenceCase: "Dealer",
            pluralSentenceCase: "Dealers",
            singularLowerCase: "dealer",
            pluralLowerCase: "dealers",
        },
        queryKeyPrefix: DEALERS_DATA_VIEWER_QUERY_KEY_PREFIX,
        calendarDataSource: "direct",
        searchColumns: EMOTORAD_DEALER_SEARCH_COLUMNS,
        searchPlaceholder: "Search dealers...",
        initialSortConfiguration: {
            revisionId: uniqueUuid`emotorad-dealers-initial-sort-revision-id`,
            lineItems: [
                {
                    columnId: "updatedAt",
                    direction: SortDirection.descending,
                },
            ],
        },
        initialVisibleFilters: {
            revisionId: uniqueUuid`b8c4e1f2-3a5d-4e6f-9a0b-1c2d3e4f5a6b`,
            lineItems: EMOTORAD_DEALER_DEFAULT_VISIBLE_FILTER_COLUMN_IDS,
        },
        filterClearConfig: {
            preservedFilterLineItemColumnIds: ["templateId"],
        },
        initialFilterConfiguration: {
            revisionId: uniqueUuid`emotorad-dealers-initial-filter-revision-id`,
            lineItems: [
                {
                    columnId: "templateId",
                    operation: FilterOperation.equals,
                    value: EMOTORAD_DEALERSHIP_TEMPLATE_ID,
                },
            ],
        },
        rowsQueryFn: async ({filterConfiguration, limit, offset, sortConfiguration, searchConfiguration}) => {
            const templateLockedFilterConfiguration = filterConfigurationWithLockedDealershipTemplate(
                filterConfiguration,
                EMOTORAD_DEALERSHIP_TEMPLATE_ID,
            );
            const effectiveFilterConfiguration = filterConfigurationWithOrganisationalUnitAccessScope(
                templateLockedFilterConfiguration,
                accessibleDealershipOrganisationalUnitIds,
            );
            if (effectiveFilterConfiguration === null) {
                return [];
            }
            const organizationalUnitsResult = await getOrganizationalUnits({
                workspaceId: workspace.id,
                offset: offset,
                limit: limit,
                complexFilters: convertOttopilotViewerFiltersToPlutoComplexFilter({
                    filterConfiguration: effectiveFilterConfiguration,
                    searchConfiguration: searchConfiguration,
                }),
                orderBy: convertOttopilotViewerSortToPlutoOrderBy(sortConfiguration),
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (organizationalUnitsResult.success !== true) {
                throw organizationalUnitsResult.error;
            }

            return organizationalUnitsResult.data.map((organizationalUnit) => {
                const dealerName = getDealerDisplayName(
                    organizationalUnit,
                    organizationalUnitTemplates,
                );
                return {
                    ...organizationalUnit,
                    title: dealerName,
                    startDate: organizationalUnit.createdAt,
                    endDate: organizationalUnit.createdAt,
                    isAlldayEvent: false,
                };
            });
        },
        rowCountQueryFn: async ({filterConfiguration, searchConfiguration}) => {
            const templateLockedFilterConfiguration = filterConfigurationWithLockedDealershipTemplate(
                filterConfiguration,
                EMOTORAD_DEALERSHIP_TEMPLATE_ID,
            );
            const effectiveFilterConfiguration = filterConfigurationWithOrganisationalUnitAccessScope(
                templateLockedFilterConfiguration,
                accessibleDealershipOrganisationalUnitIds,
            );
            if (effectiveFilterConfiguration === null) {
                return 0 as Integer;
            }
            const organizationalUnitsLengthResult = await getOrganizationalUnitsCount({
                workspaceId: workspace.id,
                complexFilters: convertOttopilotViewerFiltersToPlutoComplexFilter({
                    filterConfiguration: effectiveFilterConfiguration,
                    searchConfiguration: searchConfiguration,
                }),
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
            if (organizationalUnitsLengthResult.success !== true) {
                throw organizationalUnitsLengthResult.error;
            }

            return organizationalUnitsLengthResult.data;
        },
        columnValuesQueryFn: async ({columnId}) => {
            if (columnId === "parentId") {
                return collectDealershipParentIds(dealerships);
            }

            const fetchedValues = collectDealershipColumnValues(dealerships, columnId);
            if (columnId === EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID) {
                return filterDealershipShowroomNameColumnValues({
                    fetchedValues: fetchedValues,
                    dealerships: dealerships,
                    accessibleOrganisationalUnitIds: accessibleDealershipOrganisationalUnitIds,
                });
            }

            return fetchedValues;
        },
        columns: {
            revisionId: uniqueUuid`67181248-1de4-49b6-8fd8-664169a3dd6c`,
            lineItems: dealerTableColumns,
        },
        views: [
            {
                id: DEALERS_TABLE_VIEW_ID,
                viewType: ViewType.table,
                visibleColumns: [
                    EMOTORAD__OU__DEALERSHIP__NAME__COLUMN_ID,
                    "status",
                    "parentId",
                    ...emotoradDealerAdditionalColumns.map((column) => column.id),
                ],
            },
            {
                id: DEALERS_KANBAN_VIEW_ID,
                viewType: ViewType.kanbanBoard,
                filterFieldId: "status",
                columnConfig: [
                    {
                        id: uniqueUuid`emotorad-dealers-kanban-active`,
                        displayName: emotoradDealerStatusToDisplayName(OrganizationalUnitStatus.active),
                        background: token("color.background.accent.green.subtlest"),
                        textColor: token("color.text.accent.green"),
                        possibleValues: [OrganizationalUnitStatus.active],
                    },
                    {
                        id: uniqueUuid`emotorad-dealers-kanban-suspended`,
                        displayName: emotoradDealerStatusToDisplayName(OrganizationalUnitStatus.deleted),
                        background: token("color.background.accent.yellow.subtlest"),
                        textColor: token("color.text.accent.yellow"),
                        possibleValues: [OrganizationalUnitStatus.deleted],
                    },
                ],
                cardRenderer: (organizationalUnit) => (
                    <EmotoradDealerKanbanCard
                        organizationalUnit={organizationalUnit}
                        displayName={getDealerDisplayName(
                            organizationalUnit,
                            organizationalUnitTemplates,
                        )}
                        onViewClick={() => {
                            dealersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.read,
                                rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                rowEngagementData: organizationalUnit,
                            });
                        }}
                        onEditClick={() => {
                            dealersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                                rowEngagementType: RowEngagementType.update,
                                rowEngagementPanelType: RowEngagementPanelType.sidebar,
                                rowEngagementData: organizationalUnit,
                            });
                        }}
                    />
                ),
            },
            {
                id: DEALERS_CALENDAR_VIEW_ID,
                viewType: ViewType.calendar,
                getEventType: (organizationalUnit) => organizationalUnit.status,
                eventOpensDaySummaryModal: true,
                calendarDaySummaryModalPresentation: {
                    kind: "emotoradMySalesTeam",
                    entitySingularNoun: "Dealer",
                    entityPluralNoun: "Dealers",
                    getSecondaryLine: (organizationalUnit) => {
                        const city = organizationalUnit.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID];
                        return typeof city === "string" ? city : "";
                    },
                },
                legendItems: dealersCalendarLegendItems,
                getEventTypeLabel: getDealersCalendarTypeLabel,
                getEventColor: getDealersCalendarTypeColor,
            },
        ],
        initialViewId: DEALERS_TABLE_VIEW_ID,
        showEditActionPerRow: true,
        readPanelComponent: (row, isExpanded) => (
            <EmotoradDealerViewWithFooterComponent
                organizationalUnit={row}
                isExpanded={isExpanded}
                setCurrentlyOpenRowEngagementPanel={dealersDataViewerController.setCurrentlyOpenRowEngagementPanel}
                invalidateDealersQuery={invalidateDealersQuery}
                onClose={() => {
                    dealersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
            />
        ),
        updatePanelComponent: (row, isExpanded) => (
            <EmotoradDealerUpdatePanel
                organizationalUnit={row}
                isExpanded={isExpanded}
                invalidateDealersQuery={invalidateDealersQuery}
                onClose={() => {
                    dealersDataViewerController.setCurrentlyOpenRowEngagementPanel({
                        rowEngagementType: RowEngagementType.none,
                        rowEngagementPanelType: RowEngagementPanelType.none,
                        rowEngagementData: null,
                    });
                }}
            />
        ),
    });

    const currentView =
        dealersDataViewerController.currentViewId === null
            ? null
            : dealersDataViewerController.views.find(
                  (view) => view.id === dealersDataViewerController.currentViewId,
              );

    const showDealersPageExportMenu =
        currentView !== null &&
        currentView !== undefined &&
        (currentView.viewType === ViewType.table || currentView.viewType === ViewType.kanbanBoard);

    const {onExportPng: handleExportAllRowsAsPng, onExportPdf: handleExportAllRowsAsPdf} =
        useOttopilotExportActions({
            controller: dealersDataViewerController,

            pngFilename: "dealers-export.png",
            pdfFilename: "dealers-export.pdf",
            documentTitle: "Dealers",
            csvExportMode: {kind: "disabled"},
            onError: (message) => {
                showFlag({
                    title: "Export failed",
                    description: message,
                    appearance: "error",
                    isAutoDismiss: true,
                    autoDismissSeconds: 3,
                });
            },
        });

    return (
        <>
            <TableOnlyPage
                title="Dealers"
                titleRightSection={
                    showDealersPageExportMenu === true ? (
                        <div
                            className={
                                isMobile === true
                                    ? "relative flex flex-wrap items-center justify-end gap-2 w-full min-w-0 sm:w-auto"
                                    : "relative grid grid-flow-col auto-cols-max gap-ds-space-100"
                            }
                        >
                            {isMobile === true ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setExportDrawerOpen(true);
                                    }}
                                    className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                    style={{
                                        borderColor: token("color.border", "#091E4224"),
                                        backgroundColor: token("elevation.surface", "#FFFFFF"),
                                        color: token("color.icon", "#44546F"),
                                    }}
                                >
                                    <ShowMoreVerticalIcon label="Export options" />
                                </button>
                            ) : (
                                <AnankeDropdownMenu<HTMLButtonElement>
                                    placement="bottom-start"
                                    trigger={({triggerRef, ...triggerProps}) => (
                                        <button
                                            {...triggerProps}
                                            ref={triggerRef}
                                            type="button"
                                            className="w-[32px] h-[32px] flex items-center justify-center rounded-[3px] border"
                                            style={{
                                                borderColor: token("color.border", "#091E4224"),
                                                backgroundColor: token("elevation.surface", "#FFFFFF"),
                                                color: token("color.icon", "#44546F"),
                                            }}
                                        >
                                            <ShowMoreVerticalIcon label="Export options" />
                                        </button>
                                    )}
                                >
                                    <AnankeDropdownItemGroup>
                                        {/* <AnankeDropdownItem
                                            onClick={() => {
                                                handleExportAllRowsAsPng();
                                            }}
                                            elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                            elemAfter={
                                                <ChevronRightIcon label="" size="small" color={token("color.icon.subtle")} />
                                            }
                                        >
                                            Export as PNG
                                        </AnankeDropdownItem> */}
                                        <AnankeDropdownItem
                                            onClick={() => {
                                                handleExportAllRowsAsPdf();
                                            }}
                                            elemBefore={<DownloadIcon label="" color={token("color.icon")} />}
                                            elemAfter={
                                                <ChevronRightIcon label="" size="small" color={token("color.icon.subtle")} />
                                            }
                                        >
                                            Export as PDF
                                        </AnankeDropdownItem>
                                    </AnankeDropdownItemGroup>
                                </AnankeDropdownMenu>
                            )}
                        </div>
                    ) : undefined
                }
                bottomSection={<OttopilotDataViewer controller={dealersDataViewerController} />}
            />

            {isMobile === true && showDealersPageExportMenu === true ? (
                <BottomDrawer
                    open={exportDrawerOpen}
                    setOpen={setExportDrawerOpen}
                    title="Export"
                    fitContentOnOpen={true}
                >
                    <ExportOptionsDrawerContent
                        onExportAsPng={() => {
                            handleExportAllRowsAsPng();
                            setExportDrawerOpen(false);
                        }}
                        onExportAsPdf={() => {
                            handleExportAllRowsAsPdf();
                            setExportDrawerOpen(false);
                        }}
                    />
                </BottomDrawer>
            ) : null}
        </>
    );
}

function EmotoradDealerKanbanCard({
    organizationalUnit,
    displayName,
    onViewClick,
    onEditClick,
}: {
    organizationalUnit: OrganizationalUnit;
    displayName: string;
    onViewClick?: () => void;
    onEditClick?: () => void;
}) {
    const [isHovered, setIsHovered] = useState(false);
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();

    const parentDisplayName = useMemo(() => {
        if (organizationalUnit.parentId === null) {
            return null;
        }
        const parent = organizationalUnits.find((ou) => ou.id === organizationalUnit.parentId);
        if (parent === undefined) {
            return null;
        }
        return getDealerDisplayName(parent, organizationalUnitTemplates);
    }, [organizationalUnit.parentId, organizationalUnits, organizationalUnitTemplates]);

    const cityRaw = organizationalUnit.additionalColumnValues[EMOTORAD__OU__DEALERSHIP__CITY__COLUMN_ID];
    const city = typeof cityRaw === "string" && cityRaw.trim() !== "" ? cityRaw.trim() : null;
    const {date: createdDate} = formatDateAndTime(organizationalUnit.createdAt);
    const metaLine = [createdDate, city].filter((part): part is string => part !== null && part !== "").join(" · ");
    const box = "div" as const;

    return React.createElement(
        box,
        {
            className: "relative",
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
        },
        React.createElement(
            box,
            {
                className: "w-full shadow-sm relative group",
                style: {
                    borderRadius: token("radius.small"),
                    background: isHovered ? token("color.background.input.hovered") : token("color.background.input"),
                    boxShadow: token("elevation.shadow.raised"),
                },
            },
            React.createElement(
                box,
                {className: "w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150"},
                React.createElement(
                    box,
                    {className: "grid grid-flow-col gap-ds-space-100 justify-between"},
                    React.createElement(
                        box,
                        {className: "overflow-hidden"},
                        React.createElement(
                            box,
                            {
                                className:
                                    "text-[0.9375rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis",
                                style: {fontFamily: "Inter", color: token("color.text")},
                            },
                            displayName,
                        ),
                        parentDisplayName !== null
                            ? React.createElement(
                                  box,
                                  {
                                      className:
                                          "text-[0.8125rem] leading-ds-space-225 truncate mt-[1px] text-ds-text-subtle",
                                      style: {fontFamily: "Inter"},
                                  },
                                  `Parent: ${parentDisplayName}`,
                              )
                            : null,
                    ),
                    isHovered && (onViewClick !== undefined || onEditClick !== undefined)
                        ? React.createElement(
                              AnankeDropdownMenu,
                              {
                                  trigger: ({triggerRef, ...props}) =>
                                      React.createElement(AnankeIconButton, {
                                          type: "button",
                                          spacing: "compact",
                                          ref: triggerRef as React.Ref<HTMLButtonElement>,
                                          icon: ShowMoreHorizontalIcon,
                                          label: "",
                                          appearance: "subtle",
                                          ...props,
                                      }),
                              },
                              React.createElement(
                                  box,
                                  {className: "w-40"},
                                  onViewClick !== undefined
                                      ? React.createElement(AnankeDropdownItem, {onClick: () => onViewClick(), children: "View"})
                                      : null,
                                  onEditClick !== undefined
                                      ? React.createElement(AnankeDropdownItem, {onClick: () => onEditClick(), children: "Edit"})
                                      : null,
                              ),
                          )
                        : null,
                ),
                React.createElement(box, {className: "h-ds-space-150"}),
                React.createElement(box, {
                    className: "h-[1px] w-full",
                    style: {backgroundColor: token("color.border")},
                }),
                React.createElement(box, {className: "h-ds-space-150"}),
                React.createElement(box, {className: "text-[0.75rem] text-ds-text-subtle"}, metaLine),
            ),
        ),
    );
}
